/**
 * Extended Testimonial – 2.5 peek slider, progress bar, video modals
 */
(function () {
  'use strict';

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function getSlidesVisible(root) {
    const width = window.innerWidth;
    const custom = parseFloat(root.dataset.slidesVisible);
    if (width <= 767) return 1.15;
    if (width <= 1199) return 2.5;
    return Number.isFinite(custom) ? custom : 2.5;
  }

  function initSlider(root) {
    if (!root || root.dataset.etReady === 'true') return;

    const track = root.querySelector('[data-et-track]');
    const prevBtn = root.querySelector('[data-et-prev]');
    const nextBtn = root.querySelector('[data-et-next]');
    const progressFill = root.querySelector('[data-et-progress-fill]');
    const enableLoop = root.dataset.loop === 'true';

    if (!track) return;

    let originalCount = 0;
    let trackIndex = 0;
    let isAnimating = false;
    let animTimer = null;

    function getOriginalSlides() {
      return Array.from(track.querySelectorAll('.et-slide:not(.et-slide--clone)'));
    }

    function getGap() {
      return parseFloat(getComputedStyle(track).gap) || 20;
    }

    function getSlideStep() {
      const slide = track.querySelector('.et-slide');
      if (!slide) return 0;
      return slide.offsetWidth + getGap();
    }

    function getRealIndex() {
      if (!enableLoop || originalCount < 2) return trackIndex;
      return ((trackIndex % originalCount) + originalCount) % originalCount;
    }

    function clearAnimLock() {
      isAnimating = false;
      if (animTimer) {
        clearTimeout(animTimer);
        animTimer = null;
      }
    }

    function lockAnim() {
      isAnimating = true;
      if (animTimer) clearTimeout(animTimer);
      animTimer = setTimeout(clearAnimLock, 550);
    }

    function clearClones() {
      track.querySelectorAll('.et-slide--clone').forEach((el) => el.remove());
    }

    function updateProgress() {
      if (!progressFill || originalCount < 1) return;

      const visible = getSlidesVisible(root);
      const realIndex = getRealIndex();

      if (originalCount <= visible) {
        progressFill.style.width = '100%';
        return;
      }

      const ratio = (realIndex + visible) / originalCount;
      const percent = Math.min(100, Math.max(8, ratio * 100));
      progressFill.style.width = `${percent}%`;
    }

    function setNavVisibility(show) {
      if (prevBtn) prevBtn.hidden = !show;
      if (nextBtn) nextBtn.hidden = !show;
    }

    function setNavLoopMode() {
      const show = enableLoop && originalCount >= 2;
      setNavVisibility(show);
      if (prevBtn) prevBtn.disabled = false;
      if (nextBtn) nextBtn.disabled = false;
    }

    function setNavPagedMode(index, maxIndex) {
      const visible = getSlidesVisible(root);
      const hideNav = originalCount <= 1;

      setNavVisibility(!hideNav);

      if (prevBtn) {
        prevBtn.disabled = index <= 0;
      }
      if (nextBtn) {
        nextBtn.disabled = index >= maxIndex;
      }
    }

    function applyTransform(index, animate) {
      const step = getSlideStep();
      if (!step) return;

      track.classList.toggle('is-resetting', !animate);
      track.style.transform = `translate3d(-${index * step}px, 0, 0)`;
      trackIndex = index;

      if (!animate) {
        requestAnimationFrame(() => track.classList.remove('is-resetting'));
      }

      if (!animate && enableLoop && originalCount >= 2 && index >= originalCount) {
        applyTransform(index - originalCount, false);
      }

      updateProgress();
    }

    function buildLoop() {
      clearClones();
      originalCount = getOriginalSlides().length;

      const rootEl = root.closest('.extended-testimonial') || root;
      const visibleDesktop = parseFloat(root.dataset.slidesVisible) || 2.5;
      rootEl.style.setProperty('--et-slides-visible', String(visibleDesktop));

      if (!enableLoop || originalCount < 2) {
        trackIndex = Math.min(trackIndex, Math.max(0, originalCount - 1));
        applyTransform(trackIndex, false);
        const visible = getSlidesVisible(root);
        const maxIndex = Math.max(0, originalCount - Math.floor(visible));
        setNavPagedMode(trackIndex, maxIndex);
        return;
      }

      getOriginalSlides().forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('et-slide--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[data-a11y-dialog-show]').forEach((btn) => btn.remove());
        track.appendChild(clone);
      });

      applyTransform(trackIndex, false);
      setNavLoopMode();
    }

    function snapIfNeeded() {
      if (!enableLoop || originalCount < 2) return;
      if (trackIndex >= originalCount) {
        applyTransform(trackIndex - originalCount, false);
      }
    }

    function onTransitionEnd(event) {
      if (event.target !== track || event.propertyName !== 'transform') return;
      clearAnimLock();
      snapIfNeeded();
      updateProgress();

      if (!enableLoop || originalCount < 2) {
        const visible = getSlidesVisible(root);
        const maxIndex = Math.max(0, originalCount - Math.floor(visible));
        setNavPagedMode(trackIndex, maxIndex);
      }
    }

    function move(direction) {
      if (isAnimating || originalCount < 1) return;

      if (enableLoop && originalCount >= 2) {
        if (direction === 1 && trackIndex === originalCount - 1) {
          lockAnim();
          applyTransform(originalCount, true);
          return;
        }

        if (direction === -1 && trackIndex === 0) {
          applyTransform(originalCount, false);
          requestAnimationFrame(() => {
            lockAnim();
            applyTransform(originalCount - 1, true);
          });
          return;
        }

        lockAnim();
        applyTransform(trackIndex + direction, true);
        return;
      }

      const visible = getSlidesVisible(root);
      const maxIndex = Math.max(0, originalCount - Math.floor(visible));
      const next = Math.max(0, Math.min(trackIndex + direction, maxIndex));
      lockAnim();
      applyTransform(next, true);
      setNavPagedMode(next, maxIndex);
    }

    function refresh() {
      buildLoop();
      requestAnimationFrame(() => {
        applyTransform(trackIndex, false);
        snapIfNeeded();
        updateProgress();
      });
    }

    track.addEventListener('transitionend', onTransitionEnd);

    if (prevBtn) prevBtn.addEventListener('click', () => move(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => move(1));

    window.addEventListener('resize', debounce(refresh, 150));

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(debounce(refresh, 150));
      const viewport = root.querySelector('[data-et-viewport]');
      if (viewport) ro.observe(viewport);
    }

    root.dataset.etReady = 'true';
    refresh();
  }

  function initAll() {
    document.querySelectorAll('[data-extended-testimonial]').forEach((root) => {
      root.dataset.etReady = 'false';
      initSlider(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-extended-testimonial]');
    if (root) {
      root.dataset.etReady = 'false';
      initSlider(root);
    }
  });
})();
(function () {
  'use strict';
  function addSwipe(root) {
    if (!root || root.dataset.etSwipe === 'true') return;
    var vp = root.querySelector('[data-et-viewport]');
    var prev = root.querySelector('[data-et-prev]');
    var next = root.querySelector('[data-et-next]');
    if (!vp || !prev || !next) return;

    var x0 = 0, y0 = 0, locked = null;

    vp.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
      locked = null;
    }, { passive: true });

    vp.addEventListener('touchmove', function (e) {
      if (locked !== null || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - x0;
      var dy = e.touches[0].clientY - y0;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
    }, { passive: true });

    vp.addEventListener('touchend', function (e) {
      if (locked !== 'x') return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) < 45) return;
      var btn = dx < 0 ? next : prev;
      if (!btn.disabled && !btn.hidden) btn.click();
    }, { passive: true });

    vp.style.touchAction = 'pan-y';
    root.dataset.etSwipe = 'true';
  }

  function initSwipeAll() {
    document.querySelectorAll('[data-extended-testimonial]').forEach(addSwipe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwipeAll);
  } else {
    initSwipeAll();
  }
  document.addEventListener('shopify:section:load', initSwipeAll);
})();